import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Button, Popconfirm, Space, Tag } from 'antd';
import formatDuration from '../../../utils/formatDuration';
import type { Project } from '../../../types/project';

export const PROJECT_DRAG_TYPE = 'TRACKER_PROJECT_ROW';

type DragItem = { index: number };

type ProjectRowProps = {
  project: Project;
  index: number;
  draggable?: boolean;
  moveProject?: (dragIndex: number, hoverIndex: number) => void;
  // 整个拖拽动作结束时触发一次（不管有没有真的挪动位置），由父组件决定要不要
  // 落库——不要在 moveProject 里顺带调用它，moveProject 在拖拽过程中会触发
  // 很多次。
  onDropped?: () => void;
  onEdit: (project: Project) => void;
  onToggleArchive: (project: Project) => void;
  onDelete: (project: Project) => void;
};

const ProjectRow: React.FC<ProjectRowProps> = ({
  project,
  index,
  draggable = false,
  moveProject,
  onDropped,
  onEdit,
  onToggleArchive,
  onDelete,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag<DragItem, unknown, { isDragging: boolean }>(
    () => ({
      type: PROJECT_DRAG_TYPE,
      item: { index },
      canDrag: () => draggable,
      end: () => onDropped?.(),
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [index, draggable, onDropped]
  );

  const [, drop] = useDrop<DragItem>(
    () => ({
      accept: PROJECT_DRAG_TYPE,
      hover: (item, monitor) => {
        if (!draggable || !ref.current || !moveProject) return;

        const dragIndex = item.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) return;

        const rect = ref.current.getBoundingClientRect();
        const hoverMiddleY = (rect.bottom - rect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        const hoverY = clientOffset ? clientOffset.y - rect.top : 0;

        if (dragIndex < hoverIndex && hoverY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverY > hoverMiddleY) return;

        moveProject(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    }),
    [draggable, index, moveProject]
  );

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0"
      style={{ opacity: isDragging ? 0.4 : 1, cursor: draggable ? 'move' : 'default' }}
    >
      <span
        className="h-3 w-3 flex-none rounded-full"
        style={{ background: project.color }}
        aria-hidden
      />

      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-gray-900">{project.name}</div>
        {project.client && <div className="truncate text-xs text-gray-500">{project.client}</div>}
      </div>

      <Tag color={project.open_tasks_count > 0 ? 'blue' : 'default'}>
        共 {project.tasks_count} · 未完成 {project.open_tasks_count}
      </Tag>

      <span className="w-20 flex-none text-right text-xs tabular-nums text-gray-600">
        本周 {formatDuration(Math.round(project.week_spent_seconds / 60))}
      </span>

      <Space size="small">
        <Button size="small" type="text" onClick={() => onEdit(project)}>
          编辑
        </Button>
        <Button size="small" type="text" onClick={() => onToggleArchive(project)}>
          {project.archived ? '恢复' : '归档'}
        </Button>
        <Popconfirm
          title="删除这个项目？"
          description="项目下的任务会保留，只是退回「未归项目」。"
          okText="删除"
          cancelText="取消"
          okButtonProps={{ danger: true }}
          onConfirm={() => onDelete(project)}
        >
          <Button size="small" type="text" danger>
            删除
          </Button>
        </Popconfirm>
      </Space>
    </div>
  );
};

export default ProjectRow;
